<template>
  <section
    :id="id"
    data-shell="changes-popover"
    role="region"
    aria-label="Unpublished changes"
    class="absolute bottom-full left-1/2 mb-2 w-[380px] max-w-[calc(100vw-16px)] -translate-x-1/2 rounded-xl bg-white text-copy shadow-popover ring-1 ring-line dark:bg-panel">
    <div class="px-4 pt-3.5 pb-2.5">
      <div class="flex items-center justify-between gap-2">
        <h2 class="text-sm font-bold text-heading">
          {{ changes.length }} unpublished {{ changes.length === 1 ? 'change' : 'changes' }}
        </h2>
        <span v-if="configStore.lastMeaningfulSaveTime" class="text-[11px] whitespace-nowrap text-muted"
          >Autosaved {{ autosaved }}</span
        >
      </div>
      <div class="mt-2.5 flex gap-1" aria-label="Filter changes">
        <button
          v-for="option in filters"
          :key="option.label"
          type="button"
          :aria-pressed="filter === option.type"
          class="flex h-6 items-center gap-1.5 rounded-full px-[9px] text-xs font-medium"
          :class="filter === option.type ? 'bg-overlay text-heading' : 'text-copy hover:bg-tint'"
          @click="filter = option.type">
          <span v-if="option.type" class="size-1.5 rounded-full" :class="statusColors[option.type]" />
          {{ option.label }}
          <span class="text-muted tabular-nums">{{
            option.type ? changes.filter((change) => change.type === option.type).length : changes.length
          }}</span>
        </button>
      </div>
    </div>
    <div class="max-h-[300px] overflow-y-auto px-1.5 pb-1.5">
      <div v-for="group in groups" :key="group.category">
        <button
          type="button"
          class="flex h-[30px] w-full items-center gap-1.5 rounded-md px-2.5 text-xs font-semibold text-muted hover:bg-tint"
          :aria-expanded="expanded.has(group.category)"
          @click="toggleGroup(group.category)">
          <ChevronDownIcon v-if="expanded.has(group.category)" class="size-[11px]" />
          <ChevronRightIcon v-else class="size-[11px]" />
          {{ group.category }} <span class="ml-auto tabular-nums">{{ group.changes.length }}</span>
        </button>
        <template v-if="expanded.has(group.category)">
          <component
            :is="entry.route ? RouterLink : 'div'"
            v-for="entry in showAll.has(group.category) ? group.changes : group.changes.slice(0, 4)"
            :key="configChangeKey(entry.change)"
            :to="entry.route"
            class="flex h-[30px] items-center gap-2 rounded-md pr-2.5 pl-[29px] text-[13px]"
            :class="entry.route ? 'hover:bg-overlay' : ''"
            @click="entry.route && emit('close')">
            <span class="size-1.5 shrink-0 rounded-full" :class="statusColors[entry.change.type]" />
            <span class="min-w-0 flex-1 truncate text-heading">{{
              phrasePlainText(
                entry.change.name,
                configStore.config?.phrases ?? [],
                configStore.fullResponse?.stable?.phrases ?? []
              )
            }}</span>
            <span v-if="entry.time !== null" class="text-[11px] text-muted tabular-nums">{{
              relativeTime(entry.time)
            }}</span>
          </component>
          <button
            v-if="group.changes.length > 4 && !showAll.has(group.category)"
            type="button"
            class="pt-1 pr-2.5 pb-1.5 pl-[45px] text-xs font-semibold text-primary"
            @click="showAll.add(group.category)"
            >Show {{ group.changes.length - 4 }} more</button
          >
        </template>
      </div>
      <p v-if="!groups.length" class="px-2.5 py-3 text-xs text-muted">No {{ filterLabel }} changes</p>
    </div>
    <footer class="flex items-center justify-between border-t border-line px-4 py-2.5 text-xs">
      <button
        type="button"
        class="flex items-center gap-1.5 font-medium text-muted hover:text-primary"
        @click="openHistory">
        <ClockIcon class="size-[13px]" />Roll back…
      </button>
      <button type="button" class="font-semibold text-primary" :disabled="disabled" @click="emit('review')">
        {{ changes.length === 1 ? 'Review 1 change' : `Review all ${changes.length} changes` }} →
      </button>
    </footer>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import type { RouteLocationRaw } from 'vue-router'
import { ChevronDownIcon, ChevronRightIcon, ClockIcon } from '@heroicons/vue/24/outline'
import { useNow, useTimeAgo } from '@vueuse/core'
import type { ConfigChange } from '@dashboard/composables/useConfigChanges'
import { configChangeKey } from '@dashboard/composables/useConfigChangeTimes'
import { useVersionHistoryDrawer } from '@dashboard/composables/useVersionHistoryDrawer'
import { useConfigStore } from '@dashboard/stores/configStore'
import { phrasePlainText } from '@dashboard/services/phraseText'
import { isForm } from '@dashboard/modules/offer/services/offerUtilities'
import { resolveQuestionId } from '@dashboard/modules/segment/services/questionNavigation'
import { ROUTES } from '@dashboard/router/routeNames'

const props = defineProps<{
  id: string
  changes: ConfigChange[]
  changedAt: (change: ConfigChange) => number | null
  disabled: boolean
}>()
const emit = defineEmits<{ review: []; close: [] }>()
const configStore = useConfigStore()
const history = useVersionHistoryDrawer()
const now = useNow({ interval: 1000 })
const autosaved = useTimeAgo(() => configStore.lastMeaningfulSaveTime, { updateInterval: 1000 })
const filter = ref<ConfigChange['type'] | null>(null)
const expanded = ref(new Set<string>())
const showAll = ref(new Set<string>())
const filters: { label: string; type: ConfigChange['type'] | null }[] = [
  { label: 'All', type: null },
  { label: 'New', type: 'added' },
  { label: 'Edited', type: 'modified' },
  { label: 'Removed', type: 'deleted' }
]
const statusColors: Record<ConfigChange['type'], string> = {
  added: 'bg-positive',
  modified: 'bg-warning',
  deleted: 'bg-danger'
}
const filterLabel = computed(() => filters.find((option) => option.type === filter.value)?.label.toLowerCase())
const entityRoutes: Record<string, { name: string; param: string }> = {
  Flows: { name: ROUTES.OFFERFUNNEL, param: 'offerFunnelId' },
  Widgets: { name: ROUTES.WIDGET, param: 'widgetId' },
  Campaigns: { name: ROUTES.CAMPAIGN, param: 'campaignId' },
  'Segment Groups': { name: ROUTES.SEGMENTGROUP, param: 'segmentGroupId' },
  Goals: { name: ROUTES.GOAL, param: 'goalId' }
}
type SettingsPage = { name: string; query?: Record<string, string>; hash?: string }
const settingsRoutes: Record<string, SettingsPage> = {
  Integrations: { name: ROUTES.INTEGRATIONS },
  'Bot Protection': { name: ROUTES.SETTINGS_FORMS },
  'Widget Settings': { name: ROUTES.SETTINGS_FORMS },
  Attribution: { name: ROUTES.INTEGRATIONS, hash: '#lead-attribution' },
  Domains: { name: ROUTES.SETTINGS_WEBSITE },
  'Split Tests': { name: ROUTES.SPLIT_TESTS }
}
// `config.settings` holds fields from several settings pages; link to the page
// of the first field that differs from the published config.
const projectSettingsPages: Record<string, SettingsPage> = {
  emailWatcher: { name: ROUTES.SETTINGS_FORMS },
  jsHooks: { name: ROUTES.SETTINGS_FORMS },
  waitForConsent: { name: ROUTES.SETTINGS_PRIVACY },
  analyticsSuppressionRules: { name: ROUTES.SETTINGS_PRIVACY },
  integrations: { name: ROUTES.INTEGRATIONS, query: { tab: 'analytics' } }
}
function settingsChangePage(): SettingsPage {
  const draft: Record<string, unknown> = configStore.config?.settings ?? {}
  const live: Record<string, unknown> = configStore.fullResponse?.stable?.settings ?? {}
  const changedKey = Object.keys(projectSettingsPages).find(
    (key) => JSON.stringify(draft[key]) !== JSON.stringify(live[key])
  )
  return (changedKey && projectSettingsPages[changedKey]) || { name: ROUTES.SETTINGS_FORMS }
}
function changeRoute(change: ConfigChange): RouteLocationRaw | undefined {
  if (change.type === 'deleted') {
    return
  }
  const params: Record<string, string> = { teamPid: String(configStore.currentTeamPid) }
  const entity = entityRoutes[change.category]
  if (entity) {
    return { name: entity.name, params: { ...params, [entity.param]: change.id } }
  }
  if (change.category === 'Offers / Forms') {
    const offer = configStore.config?.rightCta?.offers?.find((item) => item.id === change.id)
    if (offer) {
      return { name: isForm(offer) ? ROUTES.FORM : ROUTES.OFFER, params: { ...params, offerId: change.id } }
    }
  }
  if (change.category === 'Questions') {
    const questionId = resolveQuestionId(configStore.config, change.id)
    if (questionId) {
      return { name: ROUTES.QUESTION, params: { ...params, questionId } }
    }
  }
  const page = change.category === 'Settings' ? settingsChangePage() : settingsRoutes[change.category]
  if (page) {
    return { ...page, params }
  }
  return undefined
}
const groups = computed(() => {
  const byCategory = new Map<
    string,
    { change: ConfigChange; time: number | null; route: RouteLocationRaw | undefined }[]
  >()
  for (const change of props.changes) {
    if (filter.value && change.type !== filter.value) {
      continue
    }
    const entries = byCategory.get(change.category) ?? []
    entries.push({ change, time: props.changedAt(change), route: changeRoute(change) })
    byCategory.set(change.category, entries)
  }
  return Array.from(byCategory, ([category, changes]) => ({
    category,
    changes: changes.sort((a, b) => (b.time ?? -1) - (a.time ?? -1))
  })).sort((a, b) => (b.changes[0]?.time ?? -1) - (a.changes[0]?.time ?? -1))
})
watch(filter, () => {
  expanded.value.clear()
  showAll.value.clear()
})
watch(
  groups,
  (value) => {
    if (!expanded.value.size && value[0]) {
      expanded.value.add(value[0].category)
    }
  },
  { immediate: true }
)
function toggleGroup(category: string) {
  if (expanded.value.has(category)) {
    expanded.value.delete(category)
  } else {
    expanded.value.add(category)
  }
}
function relativeTime(time: number) {
  const seconds = Math.max(0, Math.floor((now.value.getTime() - time) / 1000))
  if (seconds < 60) {
    return `${seconds}s`
  }
  if (seconds < 3600) {
    return `${Math.floor(seconds / 60)}m`
  }
  if (seconds < 86400) {
    return `${Math.floor(seconds / 3600)}h`
  }
  return `${Math.floor(seconds / 86400)}d`
}
function openHistory() {
  emit('close')
  history.open()
}
</script>
